import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { Lock } from "lucide-react";

export default function StudioLogin() {
  const { login, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useLang();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    const res = await login(email, password);
    setBusy(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    if (res.user.role !== "admin") {
      // Clients must use /login — keep them out of the studio entrance.
      logout();
      setError(t("studio.wrong_door"));
      return;
    }
    navigate("/admin");
  };

  return (
    <div className="min-h-screen pt-24 pb-20 bg-[var(--bg)]" data-testid="studio-login-page">
      <div className="max-w-5xl mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-12 items-center">
        <div className="hidden md:block">
          <p className="overline mb-5 inline-flex items-center gap-2">
            <Lock size={12} strokeWidth={1.6} /> {t("studio.aside.overline")}
          </p>
          <h1 className="font-serif font-light text-5xl text-[color:var(--ink)] leading-tight tracking-tight">
            {t("studio.aside.title.a")} <em className="text-[color:var(--accent)] not-italic">{t("studio.aside.title.b")}</em>
          </h1>
          <p className="mt-6 text-[color:var(--ink-2)] leading-relaxed">
            {t("studio.aside.body")}
          </p>
        </div>

        <div className="card-soft p-8 md:p-10">
          <h2 className="font-serif text-3xl text-[color:var(--ink)]">{t("studio.title")}</h2>
          <p className="text-[color:var(--ink-2)] text-sm mt-1">{t("studio.subtitle")}</p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="studio-login-form">
            <div>
              <label className="overline block mb-2">{t("common.email")}</label>
              <input
                type="email"
                required
                className="input-warm"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="studio-login-email"
                autoComplete="email"
              />
            </div>
            <div>
              <label className="overline block mb-2">{t("common.password")}</label>
              <input
                type="password"
                required
                className="input-warm"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="studio-login-password"
                autoComplete="current-password"
              />
            </div>

            {error && (
              <p className="text-sm text-[color:var(--accent-2)] bg-[color:var(--bg-2)] border border-[color:var(--line-soft)] rounded-lg px-3 py-2" data-testid="studio-login-error">
                {error}
              </p>
            )}

            <button type="submit" disabled={busy} className="btn-primary w-full justify-center mt-2" data-testid="studio-login-submit">
              {busy ? t("studio.opening") : t("studio.submit")}
            </button>
          </form>

          <p className="mt-6 text-xs text-[color:var(--ink-3)]">
            {t("studio.client_link")}{" "}
            <Link to="/login" className="underline underline-offset-4 hover:text-[color:var(--accent)]">
              {t("studio.client_signin")}
            </Link>
          </p>
          <p className="mt-2 text-xs text-[color:var(--ink-3)]">
            {t("common.or_return_to")}{" "}
            <Link to="/" className="underline underline-offset-4">{t("common.homepage")}</Link>.
          </p>
        </div>
      </div>
    </div>
  );
}
